import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, Folder, FolderOpen, Lock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  expandedWith,
  folderAncestorIds,
  folderIndentClass,
  folderTreeKeyAction,
  toggleExpanded,
  visibleFolderRows,
  type FolderTreeNode,
  type VisibleFolderRow,
} from './folder-tree'

export type { FolderTreeNode }

interface FolderTreeProps {
  folders: FolderTreeNode[]
  selectedId: string | null | undefined
  onSelect: (id: string) => void
  /**
   * Controlled expansion. Pass both when the tree lives inside a dialog whose
   * content unmounts on close but the open folders should survive a reopen.
   */
  expanded?: Set<string>
  onExpandedChange?: (expanded: Set<string>) => void
  /** A disabled folder can still be expanded and focused, but not selected. */
  isDisabled?: (folder: FolderTreeNode) => boolean
  showCounts?: boolean
  emptyText?: string
  /** Rows become drop targets only when this is given (Photos filing by drag). */
  onDropOnFolder?: (folderId: string, e: React.DragEvent) => void
  /** Trailing per-row controls, e.g. an actions menu. Clicks and keys inside never select the row. */
  renderRowActions?: (folder: FolderTreeNode) => React.ReactNode
  className?: string
  'aria-label'?: string
}

/** Row look, exported so a sibling row (the move dialog's "root") matches the tree exactly. */
export function folderRowClassName({ selected, disabled = false }: { selected: boolean; disabled?: boolean }): string {
  return cn(
    'flex h-8 select-none items-center gap-1.5 rounded-md pr-2 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
    disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
    selected && 'bg-primary/15 font-medium text-foreground',
    !selected && !disabled && 'hover:bg-muted',
  )
}

/**
 * The one Doc Control folder tree every picker renders: document picker,
 * publish/destination folder pickers and the move dialog. Keyboard follows the
 * WAI-ARIA tree pattern (arrows move and open/close, Enter/Space selects).
 */
const FolderTree: React.FC<FolderTreeProps> = ({
  folders,
  selectedId,
  onSelect,
  expanded: expandedProp,
  onExpandedChange,
  isDisabled,
  showCounts = true,
  emptyText,
  onDropOnFolder,
  renderRowActions,
  className,
  'aria-label': ariaLabel,
}) => {
  const { t } = useTranslation()
  const [ownExpanded, setOwnExpanded] = useState<Set<string>>(() => new Set())
  const expanded = expandedProp ?? ownExpanded
  const setExpanded = (next: Set<string>) => {
    if (next === expanded) return
    if (!expandedProp) setOwnExpanded(next)
    onExpandedChange?.(next)
  }

  const [dropTargetId, setDropTargetId] = useState<string | null>(null)
  const rows = useMemo(() => visibleFolderRows(folders, expanded), [folders, expanded])
  const [focusedId, setFocusedId] = useState<string | null>(null)
  const rowRefs = useRef(new Map<string, HTMLDivElement>())
  const pendingFocus = useRef<string | null>(null)

  // A pre-selected folder (e.g. a publish target taken from the workflow
  // template) may sit inside collapsed parents: open them once the tree loads.
  useEffect(() => {
    if (!selectedId) return
    const ancestors = folderAncestorIds(folders, selectedId)
    if (ancestors && ancestors.length > 0) setExpanded(expandedWith(expanded, ancestors))
    // Only when the tree itself arrives or changes — not on every expand/collapse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders])

  useEffect(() => {
    const id = pendingFocus.current
    if (!id) return
    pendingFocus.current = null
    rowRefs.current.get(id)?.focus()
  })

  const isOnScreen = (id: string | null | undefined) => !!id && rows.some((row) => row.node.id === id)
  const tabStopId = isOnScreen(focusedId) ? focusedId : isOnScreen(selectedId) ? selectedId : rows[0]?.node.id

  const select = (row: VisibleFolderRow) => {
    if (isDisabled?.(row.node)) return
    onSelect(row.node.id)
    if (row.hasChildren) setExpanded(expandedWith(expanded, [row.node.id]))
  }

  const handleKeyDown = (e: React.KeyboardEvent, row: VisibleFolderRow) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      select(row)
      return
    }
    const action = folderTreeKeyAction(rows, row.node.id, e.key)
    if (!action) return
    e.preventDefault()
    if (action.type === 'focus') {
      pendingFocus.current = action.id
      setFocusedId(action.id)
    } else {
      setExpanded(toggleExpanded(expanded, action.id))
    }
  }

  if (folders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-6 text-center text-sm text-muted-foreground">
        <Folder className="h-8 w-8 opacity-40" />
        {emptyText ?? t('DocControl_no_folders')}
      </div>
    )
  }

  return (
    <div role="tree" aria-label={ariaLabel ?? t('DocControl_folders')} className={cn('space-y-0.5', className)}>
      {rows.map((row) => {
        const { node, level, hasChildren, isExpanded } = row
        const selected = node.id === selectedId
        const disabled = !!isDisabled?.(node)
        const count = node.documentCount ?? 0
        const FolderIcon = isExpanded ? FolderOpen : Folder
        return (
          <div
            key={node.id}
            ref={(el) => {
              if (el) rowRefs.current.set(node.id, el)
              else rowRefs.current.delete(node.id)
            }}
            role="treeitem"
            aria-level={level + 1}
            aria-selected={selected}
            aria-expanded={hasChildren ? isExpanded : undefined}
            aria-disabled={disabled || undefined}
            tabIndex={node.id === tabStopId ? 0 : -1}
            title={node.name}
            className={cn(
              folderRowClassName({ selected, disabled }),
              folderIndentClass(level),
              dropTargetId === node.id && 'bg-primary/20 ring-2 ring-primary',
            )}
            onClick={() => select(row)}
            onDragOver={
              onDropOnFolder
                ? (e) => {
                    e.preventDefault()
                    setDropTargetId(node.id)
                  }
                : undefined
            }
            onDragLeave={onDropOnFolder ? () => setDropTargetId((cur) => (cur === node.id ? null : cur)) : undefined}
            onDrop={
              onDropOnFolder
                ? (e) => {
                    e.preventDefault()
                    setDropTargetId(null)
                    onDropOnFolder(node.id, e)
                  }
                : undefined
            }
            onFocus={() => setFocusedId(node.id)}
            onKeyDown={(e) => handleKeyDown(e, row)}
          >
            <span
              aria-hidden
              className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground',
                hasChildren && 'cursor-pointer hover:bg-foreground/10 hover:text-foreground',
              )}
              onClick={
                hasChildren
                  ? (e) => {
                      e.stopPropagation()
                      setExpanded(toggleExpanded(expanded, node.id))
                    }
                  : undefined
              }
            >
              {hasChildren && (isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)}
            </span>
            <FolderIcon className="h-4 w-4 shrink-0 text-amber-500" />
            <span className="min-w-0 flex-1 truncate">{node.name}</span>
            {node.restricted && <Lock className="h-3 w-3 shrink-0 text-muted-foreground" aria-label={t('DocControl_restricted_folder')} />}
            {showCounts && count > 0 && (
              <Badge variant="secondary" className={cn('shrink-0 px-2 font-medium tabular-nums', selected && 'bg-background')}>
                {count}
              </Badge>
            )}
            {renderRowActions && (
              <span className="shrink-0" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                {renderRowActions(node)}
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default FolderTree
